import classNames from 'classnames'
import { MutableRefObject, ReactElement, ReactNode, useEffect, useRef } from 'react'

interface Props {
  id: string
  show: boolean
  hide: () => void
  children: ReactNode
  className?: string
  rootId?: string
}

export function PopoverBox({ ...props }: Props): ReactElement {
  const ref = useRef<HTMLDivElement>(null)

  // Hide the popover when clicking outside the box.
  useEffect(() => {
    if (props.show) {
      const onClick = (event: Event) => didClickOutside(ref, event) && props.hide()
      document.addEventListener('mousedown', onClick)
      return () => document.removeEventListener('mousedown', onClick)
    }
  }, [props.show, props.hide])

  const className = classNames(
    'absolute',
    'z-20',
    'top-full',
    'right-0',
    'max-w-[calc(100vw-2rem)]',
    'min-w-min',
    'bg-theme-surface',
    'shadow-dark',
    'rounded-xs',
    props.className
  )

  return (
    <div aria-live="polite" id={props.id} ref={ref}>
      {props.show && <div className={className}>{props.children}</div>}
    </div>
  )
}

// Check if the user clicked outside the popover box.
export function didClickOutside(ref: MutableRefObject<HTMLDivElement | null>, event: Event): boolean {
  const el = event.target as HTMLElement | undefined

  if (!el) {
    return false
  }

  return !ref.current?.contains(el)
}
